import { Route } from '@angular/router';
import { AUTH_ROUTE_IDS, LOCALIZED_AUTH_ROUTES } from './auth-route-const';
import { LogoutGuard } from './guards/logout.guard';
import { RedirectToDashboardIfIsLoggedInGuard } from './guards/redirect-to-dashboard-if-is-logged-in.guard';

/** Options for an app's auth routes. */
export interface AuthPagesOptions {
  /** true when the routes run in the admin app (login restricted to the ADMIN role). */
  isForAdmin?: boolean;
}

/**
 * Builds the localized auth route tree reused by both apps (user/admin). The form
 * components are loaded directly; app-specific flags (`isForAdmin`,
 * `initialPasswordMode`) arrive through the route `data` and are bound to the
 * inputs via `withComponentInputBinding()`. Both apps' routers therefore have to
 * be configured with `withComponentInputBinding()`.
 *
 * @param localeId Active locale (e.g. `de`, `de-CH`); determines the paths.
 * @param options App-specific flags (default: none).
 */
export function AuthPagesRoutes(localeId: string, options: AuthPagesOptions = {}): Route {
  const locale = localeId?.split('-')[0] || 'en';
  const isForAdmin = options.isForAdmin ?? false;

  return {
    path: LOCALIZED_AUTH_ROUTES[AUTH_ROUTE_IDS.ROOT][locale],
    children: [
      {
        path: '',
        pathMatch: 'full',
        redirectTo: LOCALIZED_AUTH_ROUTES[AUTH_ROUTE_IDS.LOGIN][locale],
      },
      {
        path: '',
        loadComponent: () => import('./pages/auth-layout/auth-layout.component').then((m) => m.AuthLayoutComponent),
        children: [
          {
            path: LOCALIZED_AUTH_ROUTES[AUTH_ROUTE_IDS.LOGIN][locale],
            loadComponent: () => import('./components/login-form/login-form.component').then((m) => m.LexplusLoginFormComponent),
            canActivate: [RedirectToDashboardIfIsLoggedInGuard],
            data: { routeId: AUTH_ROUTE_IDS.LOGIN, isForAdmin },
          },
          {
            path: LOCALIZED_AUTH_ROUTES[AUTH_ROUTE_IDS.LOGOUT][locale],
            canActivate: [LogoutGuard],
            children: [],
            data: { routeId: AUTH_ROUTE_IDS.LOGOUT },
          },
          {
            path: LOCALIZED_AUTH_ROUTES[AUTH_ROUTE_IDS.TWO_FACTOR][locale],
            loadComponent: () => import('./components/two-fa-form/two-fa-form.component').then((m) => m.LexplusTwoFaFormComponent),
            canActivate: [RedirectToDashboardIfIsLoggedInGuard],
            data: { routeId: AUTH_ROUTE_IDS.TWO_FACTOR, isForAdmin },
          },
          {
            path: LOCALIZED_AUTH_ROUTES[AUTH_ROUTE_IDS.INITIAL_PASSWORD][locale],
            loadComponent: () =>
              import('./components/password-reset-form/password-reset-form.component').then((m) => m.LexplusPasswordResetFormComponent),
            canActivate: [RedirectToDashboardIfIsLoggedInGuard],
            data: { routeId: AUTH_ROUTE_IDS.INITIAL_PASSWORD, isForAdmin, initialPasswordMode: true },
          },
          {
            path: LOCALIZED_AUTH_ROUTES[AUTH_ROUTE_IDS.CHANGE_PASSWORD][locale],
            loadComponent: () =>
              import('./components/request-password-reset-form/request-password-reset-form.component').then(
                (m) => m.LexplusRequestPasswordResetFormComponent,
              ),
            canActivate: [RedirectToDashboardIfIsLoggedInGuard],
            data: { routeId: AUTH_ROUTE_IDS.CHANGE_PASSWORD, isForAdmin },
          },
          {
            path: LOCALIZED_AUTH_ROUTES[AUTH_ROUTE_IDS.RESET_PASSWORD][locale],
            loadComponent: () =>
              import('./components/password-reset-form/password-reset-form.component').then((m) => m.LexplusPasswordResetFormComponent),
            canActivate: [RedirectToDashboardIfIsLoggedInGuard],
            data: { routeId: AUTH_ROUTE_IDS.RESET_PASSWORD, isForAdmin },
          },
          {
            // Also reachable untranslated, because e-mails link to it.
            path: 'reset-password',
            loadComponent: () =>
              import('./components/password-reset-form/password-reset-form.component').then((m) => m.LexplusPasswordResetFormComponent),
            canActivate: [RedirectToDashboardIfIsLoggedInGuard],
            data: { routeId: AUTH_ROUTE_IDS.RESET_PASSWORD, isForAdmin },
          },
        ],
      },
    ],
  };
}
